import React, { useEffect, useRef } from 'react';
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useForm, useWatch } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { useScrollToBottom } from '../../../hooks/useScrollToBottom';
import { Alfred, isICTLockProvider } from '../../../utils/lockInfo';
import { ACCEPTED_IMG_MIME_TYPES, MAX_IMG_FILE_SIZE } from '../../Deliveries/constants';
import { DEFAULT_USER_PIN_LENGTH, ORGANIZATION_PROVIDER_SERVICE_TYPE, UserRole, isYardiOrganization } from '../../users/constant';
import { requestPending, requestSuccess } from '../../../utils/status';
import { GET_BUILDING, SET_GLOBAL_BUILDING } from '../../../redux/constants';
import { Form } from '../../../components/ui/Form';
import {
  ModalBodyContent,
  ModalButtonsContainer,
  ModalHeader,
  StyledModalBody,
  StyledModalV2
} from '../../../components/Modals';
import {
  addBuilding,
  clearBuilding,
  clearBuildingError,
  editBuilding,
  getAllBuildings,
  getBuildings,
  incrementBuildingEventCount,
  setbuildingImg,
  setCachedBuildings,
  setGlobalBuilding,
  setGlobalBuildingId,
  setGlobalBuildingStatus,
  setGlobalSelectedBuilding,
  setLoadingCachedBuildings
} from '../../../redux/actions/building';
import { changeOpenedModal } from '../../../redux/actions/headerModal';
import CoreFeatureFormInputs from '../Organization/CoreFeatureFormInputs';
import UserAccessFormInputs from '../Organization/UserAccessFormInputs';
import FeatureModuleFormInputs from '../Organization/FeatureModuleFormInputs';
import SyncConfigFormInputs from './SyncConfigFormInputs';
import CustomSettingFormInputs from '../Organization/CustomSettingFormInputs';
import BuildingInfoFormInputs from './BuildingInfoFormInputs';
import { formatMbFileSize } from '../../../utils/utils';
import { emailRegx, zip } from '../../../helper/Regx';
import { imageString } from '../../../helper';
import { isCobaltAmenityBookingBuilding } from '../constants';
import LoadingAvatar from '../../../components/LoadingAvatar';
import { LOADING_BLUE } from '../../../utils/colorCode';
import { ButtonBodyVariant } from '../../../components/ui/ButtonVariant';
import { setToast } from '../../../redux/actions/window';
import { searchTable, setSearchPagination, setSearchState } from '../../../redux/actions/search';
import { setUserSearch } from '../../../redux/actions/user';
import { DEFAULT_DEVICE_DOWN_NOTIFICATION_DELAY, INTERCOM_NAME_SETTINGS, INTERCOM_UNIT_NAME_DISPLAY_SETTINGS, VOPAY_ACCOUNT_STATUS } from '../../../helper/constants';
import { getOrganizationOnboardingStatus } from '../../../redux/actions/payments';
import { normalizeVopayAccountStatus } from '../../payments/PaymentUtils';
import { LOCK_PROVIDERS, SMART_LOCKER_PROVIDERS } from '../../../utils/mappings';
import { isValidPhoneNumber } from '../../../helper/phoneNumber';
import { MAX_CONTACTS_PER_BUILDING } from '../constants';

const AddEditBuilding = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const modalBodyRef = useRef(null);
  const existingHomepageImageRef = useRef('');
  const { isAtBottom } = useScrollToBottom(modalBodyRef);
  const modal = useSelector((state) => state.headerModal.modal);
  const { orgId, roleId } = useSelector((state) => state.auth);
  const { building, status, pagination } = useSelector((state) => state.building);
  const { AllOrganizations, organization, globalOrgId } = useSelector((state) => state.Organization);
  const searchState = useSelector((state) => state.search);
  const isEdit = modal === 'editBuilding';
  const hasExistingVopayAccount = Boolean(building?.vopay_account_id);
  const shouldShowVopayEmailInput = (enableVopay) => enableVopay && (!isEdit || !hasExistingVopayAccount);

  existingHomepageImageRef.current = isEdit ? (building?.homepage_image || '') : '';


  const schema = Yup.object().shape({
    name: Yup.string()
      .max(25, t('Not more than 25 characters'))
      .required(t('Name is required!')),
    contacts: Yup.array().of(
      Yup.object().shape({
        id: Yup.number(),
        contact_name: Yup.string()
          .max(100, t('Not more than 100 characters')),
        contact_email: Yup.string()
          .test('is-valid-email', t('Invalid email address'), (value) => {
            if (!value || value.trim() === '') return true;
            return emailRegx.test(value);
          }),
        phone: Yup.string()
          .test('is-valid-phone', t('Not a valid phone number!'), (value) => {
            if (!value || value.trim() === '') return true;
            return isValidPhoneNumber(value);
          }),
      })
        .test(
          'at-least-one-field',
          t('At least one contact field must be provided'),
          function (contact) {
            // Check if at least one field has a non-empty value
            const hasName = contact.contact_name && contact.contact_name.trim() !== '';
            const hasEmail = contact.contact_email && contact.contact_email.trim() !== '';
            const hasPhone = contact.phone && contact.phone.trim() !== '';

            return hasName || hasEmail || hasPhone;
          }
        )
    )
      .max(MAX_CONTACTS_PER_BUILDING, `Maximum ${MAX_CONTACTS_PER_BUILDING} contacts allowed`)
      .nullable(),
    address: Yup.string()
      .min(2, t('Address is required!'))
      .matches(/^[A-zÀ-ÿ0-9 _\- \.\,]{0,200}$/i, t('Incorrect Format,only alphanumeric values'))
      .max(200, t('Not more than 200 characters'))
      .required(t('Address is required!')),
    city: Yup.string()
      .matches(/^[a-zA-Z0-9., ]{0,25}$/i, t('Incorrect Format,only alphanumeric values'))
      .max(25, t('Not more than 25 characters'))
      .required(t('City is required!')),
    lock_provider: Yup.number()
      .required(t('Lock Provider is required'))
      .oneOf(LOCK_PROVIDERS.map((v) => v.id)),
    photo: Yup.mixed()
      .test('is-valid-type', t('Only JPG and PNG files are supported'), (value) => {
        if (!value) return true;
        return ACCEPTED_IMG_MIME_TYPES.includes(value.blob.type);
      })
      .test('is-valid-size', t('Maximum file size is', { maxFileSize: formatMbFileSize(MAX_IMG_FILE_SIZE) }), (value) => {
        if (!value) return true;
        return value.blob.size <= MAX_IMG_FILE_SIZE;
      }),
    homepage_image: Yup.mixed()
      .test('is-valid-type', t('Only JPG and PNG files are supported'), (value) => {
        if (!value || value === '') return true;
        return ACCEPTED_IMG_MIME_TYPES.includes(value.blob?.type);
      })
      .test('is-valid-size', t('Maximum file size is', { maxFileSize: formatMbFileSize(MAX_IMG_FILE_SIZE) }), (value) => {
        if (!value || value === '') return true;
        return value.blob?.size <= MAX_IMG_FILE_SIZE;
      })
      .test('required-when-v2-home', t('V2 home page image is required'), function (value) {
        if (!this.parent.enable_v2_home_page) return true;
        if (value?.blob) return true;
        if (value === '') return false;
        return !!existingHomepageImageRef.current;
      }),
    org_id: Yup.string()
      .test('is-required', t('Organization is required') + '!', (value, context) => {
        if (roleId !== UserRole.SUPER_ADMIN) return true;

        if (value < 0) return false;

        if (!value || value === '') return false;

        return true;
      })
      .typeError(t('Organization is required') + '!'),
    state: Yup.string()
      .required(t('State is required!'))
      .test('is-required', t('State is required!'), (value, context) => {
        if (value === 'Select one') return false;
        if (context.parent.country <= 0) return false;
        return true;
      }),

    zip: Yup.string()
      .matches(zip, t('Invalid ZIP/Postal code'))
      .min(4, t('Expects minimum 5 characters'))
      .max(10, t('Not more than 10 characters'))
      .required(t('Zip is required!')),
    country: Yup.string()
      .required(t('Country is required!'))
      .test('is-required', t('Country is required!'), (value, context) => {
        if (value <= 0) {
          context.parent.state = 'Select one';
          return false;
        }
        return true;
      }),
    enable_posts: Yup.bool().required('Missing Enable Posts'),
    enable_offers: Yup.bool().required('Missing Enable Offers'),
    enable_booking: Yup.bool().required('Missing Enable Bookings'),
    enable_lock_screen: Yup.bool().required('Missing Enable Lock Screen'),
    enable_service_requests: Yup.bool().required('Missing Enable Service Requests'),
    show_user_pin_resident: Yup.bool().required('Missing Show User Pin Resident'),
    show_user_pin_admin: Yup.bool().required('Missing Show User Pin Admin'),
    show_user_pin_to_admin: Yup.bool().required('Missing Show User Pin to Admin'),
    org_allow_edits: Yup.bool().required('Missing Organization Allow Edit'),
    enable_add_guests: Yup.bool().required('Missing Organization Enable Add Guests'),
    enable_add_co_residents: Yup.bool().required('Missing Organization Enable Add CoResidents'),
    allow_resident_credential_issuance: Yup.bool().required('Missing Allow Resident Credential Issuance'),
    enable_thermostats: Yup.bool().required('Missing Enable Thermostats'),
    enable_leak_detectors: Yup.bool().required('Missing Enable Leak Detectors'),
    enable_deliveries: Yup.bool().required('Missing Enable Deliveries'),
    enable_chargers: Yup.bool().required('Missing Enable Chargers'),
    enable_document_storage: Yup.bool().required('Missing Enable Document Storage'),
    enable_events: Yup.bool().required('Missing Enable Events'),
    enable_v2_home_page: Yup.bool().required('Missing Enable V2 Home Page'),
    enable_chat: Yup.bool().required('Missing Enable Chat'),
    display_low_connectivity_banner: Yup.bool().required('Missing Display Low Connectivity Banner'),
    enable_akuvox: Yup.bool().required('Missing Enable Akuvox'),
    sync_akuvox_co_residents: Yup.bool().required('Missing Sync Akuvox Co-Residents'),
    enable_akuvox_private_pins: Yup.bool().required('Missing Enable Akuvox Private PINs'),
    enable_edit_intercom_pin: Yup.bool().required('Missing Enable Edit Intercom PIN'),
    intercom_name_format: Yup.string().required('Missing Intercom Name Format'),
    intercom_unit_name_display: Yup.string().required('Missing Intercom Unit Name Display'),
    enable_security_cameras: Yup.bool().required('Missing Enable Security Cameras'),
    enable_ai_nvr: Yup.bool().required('Missing Enable AI NVR'),
    suite_enabled: Yup.bool().required('Missing Enable Multi Lock'),
    enable_license_plates: Yup.bool().required('Missing Enable License Plates'),
    sync_config: Yup.object({
      enable_yardi_users: Yup.bool(),
      enable_yardi_work_requests: Yup.bool(),
      enable_yardi_send_welcome_email: Yup.bool(),
      pms_sync_future_residents: Yup.bool(),
      pms_sync_pm_emails: Yup.bool(),
      pms_sync_pets: Yup.bool(),
      enable_entrata_multi_building_property: Yup.bool(),
      enable_auto_gx_sync_acl: Yup.bool(),
      enable_auto_salto_sync_acl: Yup.bool(),
    }),
    enable_vopay: Yup.bool().required('Missing Enable Vopay'),
    building_email: Yup.string().test('vopay-building-email', function (value) {
      const { enable_vopay } = this.parent;
      const isVisible = shouldShowVopayEmailInput(enable_vopay);

      if (!isVisible) {
        return true;
      }

      const trimmedEmail = (value || '').trim();
      if (!trimmedEmail) {
        return this.createError({ message: t('Email is required!') });
      }

      if (!emailRegx.test(trimmedEmail)) {
        return this.createError({ message: t('Invalid email address') });
      }

      return true;
    }),
    smart_locker_provider: Yup.number()
      .required('Missing Smart Locker Provider')
      .oneOf(SMART_LOCKER_PROVIDERS.map((v) => v.id)),
    enable_custom_service_request_categories: Yup.bool().required('Missing Enable Custom Service Request Categories'),
    enable_mfa: Yup.bool().required('Missing Enable MFA'),
    enable_automatic_booking_access: Yup.bool(),
    user_pin_len: Yup.number()
      .required()
      .min(Alfred.pinMinLength, t('Minimum Pin Length is 4'))
      .max(Alfred.pinMaxLength, t('Maximum Pin Length is 10')),
    service_request_url: Yup.string().url('Invalid URL'),
    payment_redirect_url: Yup.string().url('Invalid URL'),
    notification_delay_device_down: Yup.number()
      .default(DEFAULT_DEVICE_DOWN_NOTIFICATION_DELAY)
      .typeError(t('Device Down Notification Delay must be a valid number'))
      .required(t('Device Down Notification Delay is required'))
      .min(1, t('Minimum delay is 1 minute'))
      .max(44640, t('Maximum delay is 44640 minutes (1 month)'))
      .integer(t('Device Down Notification Delay must be a whole number')),
  });

  const form = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      org_id: roleId !== UserRole.SUPER_ADMIN ? orgId : null,
      name: '',
      contacts: [],
      address: '',
      city: '',
      state: null,
      zip: '',
      country: 'CA',
      photo: null,
      homepage_image: null,
      enable_posts: organization?.config.enable_posts,
      enable_offers: organization?.config.enable_offers,
      enable_booking: organization?.config.enable_booking,
      enable_lock_screen: organization?.config.enable_lock_screen,
      enable_service_requests: organization?.config.enable_service_requests,
      show_user_pin_admin: organization?.config.show_user_pin_admin,
      show_user_pin_resident: organization?.config.show_user_pin_resident,
      show_user_pin_to_admin: organization?.config.show_user_pin_to_admin,
      enable_add_guests: !!organization?.config.enable_add_guests,
      enable_add_co_residents: !!organization?.config.enable_add_co_residents,
      allow_resident_credential_issuance: !!organization?.config.allow_resident_credential_issuance,
      service_request_url: organization?.config.service_request_url,
      payment_redirect_url: organization?.config.payment_redirect_url || '',
      org_allow_edits: organization?.config.org_allow_edits,
      user_pin_len: DEFAULT_USER_PIN_LENGTH,
      enable_thermostats: organization?.config.enable_thermostats,
      enable_leak_detectors: organization?.config.enable_leak_detectors,
      enable_deliveries: organization?.config.enable_deliveries,
      enable_chargers: !!organization?.config.enable_chargers,
      enable_document_storage: !!organization?.config.enable_document_storage,
      enable_events: !!organization?.config.enable_events,
      enable_v2_home_page: !!organization?.config.enable_v2_home_page,
      enable_chat: !!organization?.config.enable_chat,
      display_low_connectivity_banner: false,
      enable_akuvox: organization?.config.enable_akuvox,
      sync_akuvox_co_residents: organization?.config.sync_akuvox_co_residents,
      enable_akuvox_private_pins: organization?.config.enable_akuvox_private_pins,
      enable_edit_intercom_pin: !!organization?.config.enable_edit_intercom_pin,
      intercom_name_format: organization?.config.intercom_name_format || INTERCOM_NAME_SETTINGS.FULL_NAME.value,
      intercom_unit_name_display: organization?.config.intercom_unit_name_display || INTERCOM_UNIT_NAME_DISPLAY_SETTINGS.NONE.value,
      enable_security_cameras: organization?.config.enable_security_cameras,
      enable_ai_nvr: false,
      suite_enabled: organization?.config.suite_enabled,
      enable_license_plates: organization?.config.enable_license_plates,
      sync_config: {
        enable_yardi_users: false,
        enable_yardi_work_requests: false,
        enable_yardi_send_welcome_email: false,
        pms_sync_future_residents: false,
        pms_sync_pm_emails: false,
        pms_sync_pets: false,
        enable_entrata_multi_building_property: false,
        enable_auto_gx_sync_acl: false,
        enable_auto_salto_sync_acl: false,
      },
      enable_custom_service_request_categories: organization?.config.enable_custom_service_request_categories,
      notification_delay_device_down: organization?.config.notification_delay_device_down || DEFAULT_DEVICE_DOWN_NOTIFICATION_DELAY,
      enable_vopay: false,
      building_email: '',
      smart_locker_provider: 0,
      lock_provider: organization?.config.lock_provider,
      enable_mfa: organization?.config?.enable_mfa ?? false,
      enable_automatic_booking_access: false,
    }
  });

  useEffect(() => {
    if (isEdit && building && status === requestSuccess(GET_BUILDING)) {
      form.reset({
        org_id: building.org_id,
        name: building.name,
        contacts: building.contacts?.map(contact => ({
          id: contact.id,
          contact_name: contact.contact_name || '',
          contact_email: contact.contact_email || '',
          phone: contact.phone || ''
        })) || [],
        address: building.address,
        city: building.city,
        state: building.state,
        zip: building.zip,
        country: building.country,
        photo: null,
        homepage_image: null,
        enable_posts: !!building.config.enable_posts,
        enable_offers: !!building.config.enable_offers,
        enable_booking: !!building.config.enable_booking,
        enable_lock_screen: !!building.config.enable_lock_screen,
        enable_service_requests: !!building.config.enable_service_requests,
        show_user_pin_admin: !!building.config.show_user_pin_admin,
        show_user_pin_resident: !!building.config.show_user_pin_resident,
        show_user_pin_to_admin: !!building.config.show_user_pin_to_admin,
        enable_add_guests: !!building?.config.enable_add_guests,
        enable_add_co_residents: !!building?.config.enable_add_co_residents,
        allow_resident_credential_issuance: !!building?.config.allow_resident_credential_issuance,
        service_request_url: building.config.service_request_url,
        payment_redirect_url: building.config.payment_redirect_url || '',
        org_allow_edits: !!building.config.org_allow_edits,
        user_pin_len: building.config.user_pin_len,
        enable_thermostats: !!building.config.enable_thermostats,
        enable_leak_detectors: !!building.config.enable_leak_detectors,
        enable_deliveries: !!building.config.enable_deliveries,
        enable_chargers: !!building.config.enable_chargers,
        enable_document_storage: !!building.config.enable_document_storage,
        enable_events: !!building.config.enable_events,
        enable_v2_home_page: !!building.config.enable_v2_home_page,
        enable_chat: !!building.config.enable_chat,
        display_low_connectivity_banner: !!building.config.display_low_connectivity_banner,
        enable_akuvox: !!building.config.enable_akuvox,
        sync_akuvox_co_residents: !!building.config.sync_akuvox_co_residents,
        enable_akuvox_private_pins: !!building.config.enable_akuvox_private_pins,
        enable_edit_intercom_pin: !!building.config.enable_edit_intercom_pin,
        intercom_name_format: building.config.intercom_name_format || INTERCOM_NAME_SETTINGS.FULL_NAME.value,
        intercom_unit_name_display: building.config.intercom_unit_name_display || INTERCOM_UNIT_NAME_DISPLAY_SETTINGS.NONE.value,
        enable_security_cameras: !!building.config.enable_security_cameras,
        enable_ai_nvr: !!building.config.enable_ai_nvr,
        suite_enabled: !!building.config.suite_enabled,
        enable_license_plates: !!building.config.enable_license_plates,
        sync_config: {
          enable_yardi_users: !!building.sync_config?.enable_yardi_users,
          enable_yardi_work_requests: !!building.sync_config?.enable_yardi_work_requests,
          enable_yardi_send_welcome_email: !!building.sync_config?.enable_yardi_send_welcome_email,
          pms_sync_future_residents: !!building.sync_config?.pms_sync_future_residents,
          pms_sync_pm_emails: !!building.sync_config?.pms_sync_pm_emails,
          pms_sync_pets: !!building.sync_config?.pms_sync_pets,
          enable_entrata_multi_building_property: !!building.sync_config?.enable_entrata_multi_building_property,
          enable_auto_gx_sync_acl: !!building.sync_config?.enable_auto_gx_sync_acl,
          enable_auto_salto_sync_acl: !!building.sync_config?.enable_auto_salto_sync_acl,
        },
        enable_custom_service_request_categories: !!building.config.enable_custom_service_request_categories,
        notification_delay_device_down: building.config.notification_delay_device_down || DEFAULT_DEVICE_DOWN_NOTIFICATION_DELAY,
        enable_vopay: !!building.config.enable_vopay,
        building_email: building.buildingEmail || building.building_email || '',
        smart_locker_provider: building.config.smart_locker_provider ?? 0,
        lock_provider: building.config.lock_provider,
        enable_mfa: building.config.enable_mfa,
        enable_automatic_booking_access: !!building.config.enable_automatic_booking_access,
      });
    }
  }, [isEdit, building, status]);

  const closeModal = () => {
    if (isEdit) {
      dispatch(clearBuilding());
    }
    dispatch(
      changeOpenedModal({
        modal: ''
      })
    );
    dispatch(clearBuildingError());
  };

  const watchPhoto = form.watch('photo');
  const watchHomepageImage = form.watch('homepage_image');
  const lockProvider = useWatch({
    control: form.control,
    name: 'lock_provider'
  });
  const isIctBuilding = isICTLockProvider(lockProvider);

  // The sync settings shown depend on the building's org PMS (Yardi or Entrata).
  const watchedOrgId = useWatch({
    control: form.control,
    name: 'org_id'
  });
  const buildingOrgId = Number(isEdit ? building?.org_id : (watchedOrgId ?? orgId));
  const buildingOrg =
    AllOrganizations?.find((org) => Number(org.id) === buildingOrgId) ||
    (Number(organization?.id) === buildingOrgId ? organization : null);
  const isYardiOrg = Boolean(isYardiOrganization(buildingOrg));
  const isEntrataOrg = buildingOrg?.prov_service === ORGANIZATION_PROVIDER_SERVICE_TYPE.ENTRATA;

  const shouldDisableSubmitButton = (form, isEdit) => {
    const valuesHaveChanged =
      Object.keys(form.formState.dirtyFields).length > 0 ||
      Boolean(watchPhoto?.fileName) ||
      watchPhoto === '' ||
      Boolean(watchHomepageImage?.fileName) ||
      watchHomepageImage === '';
    return form.formState.isSubmitting || !form.formState.isValid || (isEdit && !valuesHaveChanged);
  }

  const updateBuildingSearchTable = () => {
    dispatch(
      searchTable({
        entity: 'buildings',
        orgID: globalOrgId,
        body: {
          search: searchState.search,
          minCreationDate: searchState.minCreationDate,
          maxCreationDate: searchState.maxCreationDate,
          category: searchState.category,
          building_id: searchState.building_id,

          filters: searchState.filters,
          ordering: searchState.ordering,
          pagination: searchState.pagination
        },
        success: (res) => {
          dispatch(
            setSearchState({
              currentData: res.data.data ?? [],
              currentPaginationData: res.data.pagination
            })
          );

          if (!res.data.data) {
            dispatch(setUserSearch({ page: 1 }));
            dispatch(
              setSearchPagination({
                page: 1,
                limit: 20
              })
            );
          }
        }
      })
    );
  };

  const updateGlobalBuilding = (orgID, buildingRes) => {
    dispatch(
      setGlobalBuildingStatus({
        status: requestPending(SET_GLOBAL_BUILDING)
      })
    );
    dispatch(
      setGlobalBuilding({
        id: orgID
      })
    );
    dispatch(
      setGlobalBuildingId({
        id: buildingRes.data.id
      })
    );
    dispatch(setGlobalSelectedBuilding(buildingRes.data));
  };

  const addOrEditBuilding = (values, { photoUrl = '', homepageImageUrl = '' } = {}) => {
    const cobaltBuilding = isEdit && isCobaltAmenityBookingBuilding(building?.id);
    const buildingConfig = {
      enable_posts: values.enable_posts,
      enable_offers: values.enable_offers,
      enable_booking: values.enable_booking,
      enable_lock_screen: values.enable_lock_screen,
      enable_service_requests: values.enable_service_requests,
      show_user_pin_admin: values.show_user_pin_admin,
      show_user_pin_resident: values.show_user_pin_resident,
      show_user_pin_to_admin: values.show_user_pin_to_admin,
      enable_add_guests: values.enable_add_guests,
      enable_add_co_residents: values.enable_add_co_residents,
      allow_resident_credential_issuance: isIctBuilding ? false : values.allow_resident_credential_issuance,
      service_request_url: values.service_request_url,
      payment_redirect_url: values.payment_redirect_url,
      org_allow_edits: values.org_allow_edits,
      user_pin_len: values.user_pin_len,
      enable_thermostats: values.enable_thermostats,
      enable_leak_detectors: values.enable_leak_detectors,
      enable_deliveries: values.enable_deliveries,
      enable_chargers: values.enable_chargers,
      enable_document_storage: values.enable_document_storage,
      enable_events: values.enable_events,
      enable_v2_home_page: values.enable_v2_home_page,
      enable_chat: values.enable_chat,
      display_low_connectivity_banner: !!values.display_low_connectivity_banner,
      enable_akuvox: values.enable_akuvox,
      sync_akuvox_co_residents: values.sync_akuvox_co_residents,
      enable_akuvox_private_pins: values.enable_akuvox_private_pins,
      enable_edit_intercom_pin: !!(
        values.enable_akuvox &&
        values.enable_akuvox_private_pins &&
        values.enable_edit_intercom_pin
      ),
      intercom_name_format: values.intercom_name_format,
      intercom_unit_name_display: values.intercom_unit_name_display,
      enable_security_cameras: values.enable_security_cameras,
      enable_ai_nvr: values.enable_ai_nvr,
      suite_enabled: values.suite_enabled,
      enable_license_plates: values.enable_license_plates,
      enable_custom_service_request_categories: values.enable_custom_service_request_categories,
      notification_delay_device_down: values.notification_delay_device_down || DEFAULT_DEVICE_DOWN_NOTIFICATION_DELAY,
      enable_vopay: values.enable_vopay,
      smart_locker_provider: values.smart_locker_provider,
      lock_provider: values.lock_provider,
      enable_mfa: values.enable_mfa,
      enable_automatic_booking_access: cobaltBuilding
        ? !!values.enable_automatic_booking_access
        : (isEdit ? !!building?.config?.enable_automatic_booking_access : false),
    };

    if (isEdit) {
      buildingConfig.security_enabled = building.config.security_enabled;
      buildingConfig.enable_global_master_pin = building.config.enable_global_master_pin;
    }

    const shouldDeleteExistingPhoto = isEdit && values.photo === '';
    const shouldDeleteExistingHomepageImage = isEdit && values.homepage_image === '';

    const buildingData = {
      name: values.name.trim(),
      contacts: values.contacts?.map(contact => ({
        ...(contact.id && { id: contact.id }), // Only include id if it exists
        contact_name: contact.contact_name?.trim() || '',
        contact_email: contact.contact_email?.trim() || '',
        phone: contact.phone?.trim() || ''
      })) || [],
      address: values.address.trim(),
      city: values.city.trim(),
      state: values.state,
      zip: values.zip,
      country: values.country,
      photo: shouldDeleteExistingPhoto ? '' : photoUrl || (isEdit ? building.photo : '') || '',
      homepage_image: shouldDeleteExistingHomepageImage
        ? ''
        : homepageImageUrl || (isEdit ? building.homepage_image : '') || '',
      config: buildingConfig
    };

    // sync_config is super admin only (api-admin drops it from anyone else). Fields not sent
    // (automatic_archive_yardi_move_out) keep their stored value.
    if (roleId === UserRole.SUPER_ADMIN) {
      buildingData.sync_config = Object.fromEntries(
        Object.entries(values.sync_config || {}).map(([key, value]) => [key, !!value])
      );
    }

    if (shouldShowVopayEmailInput(values.enable_vopay)) {
      buildingData.buildingEmail = values.building_email?.trim() || '';
    }

    if (!isEdit) {
      buildingData.org_id = parseInt(values.org_id, 10);
    }

    if (isEdit) {
      dispatch(
        editBuilding({
          id: building.id,
          body: buildingData,
          success: (res) => {
            dispatch(
              getBuildings({
                id: building.org_id,
                page: pagination.currentPage
              })
            );
            dispatch(
              getAllBuildings({
                id: building.org_id,
              })
            );
            updateBuildingSearchTable();
            dispatch(
              setToast({
                toastShow: true,
                toastMessage: `Building ${buildingData.name} has been successfully updated.`
              })
            );
            closeModal();
          },
          fail: () => {
            dispatch(
              setToast({
                toastShow: true,
                toastMessage: t('Failed to edit building'),
                modal: 'errorToast'
              })
            );
          }
        })
      );
    } else {
      dispatch(
        addBuilding({
          body: buildingData,
          success: (res) => {
            dispatch(incrementBuildingEventCount());
            if (roleId === UserRole.SUPER_ADMIN) {
              dispatch(setLoadingCachedBuildings({ orgId, isLoading: true }));
              dispatch(
                setCachedBuildings({
                  id: orgId,
                  success: () => {
                    dispatch(setLoadingCachedBuildings({ orgId, isLoading: false }));
                  }
                })
              );
            }

            const valueOrgId = parseInt(buildingData.org_id, 10);

            if (globalOrgId === valueOrgId && roleId === UserRole.SUPER_ADMIN) {
              updateGlobalBuilding(globalOrgId, res);
            } else if (parseInt(orgId, 10) === valueOrgId && roleId === UserRole.ADMIN) {
              updateGlobalBuilding(orgId, res);
            }

            updateBuildingSearchTable();
            dispatch(
              getBuildings({
                id: buildingData.org_id,
                page: 1
              })
            );
            dispatch(
              setToast({
                toastShow: true,
                toastMessage: `Building ${buildingData.name} has been successfully created.`
              })
            );
            closeModal();
          },
          fail: () => {
            dispatch(
              setToast({
                toastShow: true,
                toastMessage: t('Failed to create building'),
                modal: 'errorToast'
              })
            );
          }
        })
      );
    }
  };

  const uploadBuildingImage = (imageValue, failureMessage) =>
    new Promise((resolve, reject) => {
      const imageData = new FormData();
      imageData.append('image', imageValue.blob, imageValue.fileName);

      dispatch(
        setbuildingImg({
          body: imageData,
          success: (res) => resolve(res.data.filename),
          fails: () => {
            dispatch(
              setToast({
                toastShow: true,
                toastMessage: failureMessage,
                modal: 'errorToast'
              })
            );
            reject(new Error(failureMessage));
          }
        })
      );
    });

  const handleSubmit = async (values) => {
    try {
      const photoUrl = values.photo?.blob
        ? await uploadBuildingImage(values.photo, t('Failed to upload building avatar'))
        : '';
      const homepageImageUrl = values.homepage_image?.blob
        ? await uploadBuildingImage(values.homepage_image, t('Failed to upload V2 home page image'))
        : '';

      addOrEditBuilding(values, { photoUrl, homepageImageUrl });
    } catch {
    }
  };

  useEffect(() => {
    if (!organization.id || !organization.config?.enable_vopay) {
      return
    }

    dispatch(getOrganizationOnboardingStatus({
      id: organization.id,
      success: (req) => {
        if (normalizeVopayAccountStatus(req.data.status) !== VOPAY_ACCOUNT_STATUS.APPROVED) {
          form.setValue('enable_vopay', false, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true
          });
        }
      },
      fail: () => {
        dispatch(
          setToast({
            toastShow: true,
            toastMessage: t('Failed to get organization onboarding status!'),
            modal: 'errorToast'
          })
        );
      }
    }))
  }, [organization.id, organization.config?.enable_vopay, dispatch, t]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <StyledModalV2 show={modal === 'addBuilding' || modal === 'editBuilding'} side width="550px">
          {status === requestPending(GET_BUILDING) ? (
            <LoadingAvatar color={LOADING_BLUE} />
          ) : (
            <>
              <StyledModalBody innerRef={(el) => (modalBodyRef.current = el)}>
                <ModalHeader title={isEdit ? t('Edit Building') : t('Add Building')} v2 />
                <ModalBodyContent>
                  <BuildingInfoFormInputs
                    form={form}
                    roleId={roleId}
                    organizations={AllOrganizations}
                    existingLogoUrl={isEdit && building.photo ? building.photo : ""}
                    isEdit={isEdit}
                  />
                  {roleId === UserRole.SUPER_ADMIN && (
                    <>
                      <CoreFeatureFormInputs form={form} />
                      <UserAccessFormInputs form={form} />
                      <FeatureModuleFormInputs
                        form={form}
                        type={"building"}
                        isEditBuilding={isEdit}
                        buildingId={isEdit ? building?.id : null}
                        hasExistingVopayAccount={hasExistingVopayAccount}
                        existingHomepageImageUrl={
                          isEdit && building.homepage_image
                            ? imageString(building.homepage_image)
                            : ''
                        }
                      />
                      <SyncConfigFormInputs
                        form={form}
                        isYardi={isYardiOrg}
                        isEntrata={isEntrataOrg}
                        lockProvider={lockProvider}
                      />
                      <CustomSettingFormInputs form={form} />
                    </>
                  )}
                </ModalBodyContent>
              </StyledModalBody>

              <ModalButtonsContainer gapY="8px" justify="flex-end" isAtBottom={isAtBottom}>
                <ButtonBodyVariant type="button" variant="secondary" onClick={closeModal}>{t('Cancel')}</ButtonBodyVariant>
                <ButtonBodyVariant type="submit" disabled={shouldDisableSubmitButton(form, isEdit)}>{t(isEdit ? 'Update' : 'Create')}</ButtonBodyVariant>
              </ModalButtonsContainer>
            </>
          )}

        </StyledModalV2>
      </form>
    </Form>
  );
};

export default AddEditBuilding;
